<template>
	<!-- overlay di caricamento -->
	<div v-if="tourLoading" class="tour-overlay">
		<div class="tour-spinner"></div>
	</div>

	<!-- NAV -->
	<aside :class="['sidebar', { open: isOpen, desk: desktop }]">
		<!-- LOGO + toggle + 🔔 -->
		<header class="hdr">
			<RouterLink to="/devices" class="logo">
				<img src="@/assets/logo_archimede.svg" alt="Archimede" />
			</RouterLink>

			<!-- campanella solo desktop -->
			<NotificationBell v-if="desktop && isOpen" placement="sidebar" />

			<button
				class="tgl"
				@click="$emit('toggle')"
				:title="isOpen ? 'Chiudi menu' : 'Apri menu'"
			>
				{{ isOpen ? "×" : "☰" }}
			</button>
		</header>

		<!-- MENU -->
		<nav class="menu">
			<draggable
				v-model="navItems"
				item-key="key"
				:animation="150"
				@end="saveOrder"
			>
				<template #item="{ element }">
					<div class="nav-row">
						<!-- Devices con submenu -->
						<template v-if="element.key === 'devices'">
							<RouterLink
								to="/devices"
								:class="['item', element.cls]"
								@click="handleDeviceClick"
							>
								<component :is="element.icon" class="ico" />
								<span v-show="isOpen || !desktop">{{ element.label }}</span>
								<ChevronIcon v-if="isOpen || !desktop" :class="{ rot: openSub }" />
							</RouterLink>
							<div v-if="isOpen && openSub" class="sub">
								<button class="item sub-i ico-editor" @click="showEditorPicker = true">
									<EditIcon class="ico" /><span>Editor</span>
								</button>
								<DevicePickerModal
									v-if="showEditorPicker"
									target="Editor"
									@close="showEditorPicker = false"
								/>
								<button class="item sub-i ico-data" @click="showDataPicker = true">
									<DatabaseIcon class="ico" /><span>Data</span>
								</button>
								<DevicePickerModal
									v-if="showDataPicker"
									target="Data"
									@close="showDataPicker = false"
								/>
							</div>
						</template>

						<!-- Voci normali -->
						<RouterLink v-else :to="element.to" :class="['item', element.cls]">
							<component :is="element.icon" class="ico" />
							<span v-show="isOpen || !desktop">{{ element.label }}</span>
						</RouterLink>
					</div>
				</template>
			</draggable>
		</nav>

		<RouterLink to="/account" class="item ico-account">
			<CircleUserIcon class="ico" />
			<span v-show="isOpen || !desktop">Account</span>
		</RouterLink>

		<button
			class="item ico-help"
			:disabled="!canStartHelp"
			:title="canStartHelp ? 'Guida rapida' : 'Nessuna guida in questa pagina'"
			@click="startHelp"
		>
			<HelpCircleIcon class="ico" />
			<span v-show="isOpen || !desktop">Guida rapida</span>
		</button>

		<!-- LOGOUT -->
		<RouterLink to="/logout">
			<a class="item logout" @click.prevent="handleLogout">
				<LogOutIcon class="ico" />
				<span v-show="isOpen || !desktop">Logout</span>
			</a>
		</RouterLink>
	</aside>

	<!-- hamburger (<lg) -->
	<button
		v-if="!desktop && !isOpen"
		class="hamb"
		@click="$emit('toggle')"
		:title="!isOpen ? 'Apri menu' : 'Chiudi menu'"
	>
		☰
	</button>

	<v-tour
		name="sidebarTour"
		:steps="computedSidebarSteps"
		:callbacks="sidebarCallbacks"
	/>
</template>

<script setup lang="ts">
import axios from "@/axiosInstance";
import router, { authIsCached, setAuth } from "@/router";
import {
	AlertTriangle as AlertTriangleIcon,
	ChevronRight as ChevronIcon,
	CircleUser as CircleUserIcon,
	ClipboardMinus as ReportIcon,
	Cpu as CpuIcon,
	Database as DatabaseIcon,
	Edit as EditIcon,
	Globe as GlobeIcon,
	HelpCircle as HelpCircleIcon,
	Home as HomeIcon,
	LogOut as LogOutIcon,
	Map as MapIcon,
} from "lucide-vue-next";
import {
	computed,
	inject,
	markRaw,
	nextTick,
	onBeforeUnmount,
	onMounted,
	ref,
	watch,
} from "vue";
import { RouterLink, useRoute } from "vue-router";
import { useToast } from "vue-toast-notification";
import draggable from "vuedraggable";
import DevicePickerModal from "./DevicePickerModal.vue";
import NotificationBell from "./NotificationBell.vue";

const props = defineProps<{ isOpen: boolean; desktop: boolean }>();
defineEmits(["toggle"]);

/* ---- nav items riordinabili ---- */
const LS_ORDER_KEY = "sidebar-nav-order";

const ALL_NAV_ITEMS = [
	{ key: "dashboard", to: "/dashboard", icon: markRaw(HomeIcon), label: "Dashboard", cls: "ico-home" },
	{ key: "devices", to: "/devices", icon: markRaw(CpuIcon), label: "Devices", cls: "ico-devices" },
	{ key: "live-maps", to: "/live-maps", icon: markRaw(MapIcon), label: "Live Maps", cls: "ico-live-maps" },
	{ key: "alerts", to: "/alerts", icon: markRaw(AlertTriangleIcon), label: "Alert", cls: "ico-alerts" },
	{ key: "reports", to: "/reports", icon: markRaw(ReportIcon), label: "Reports", cls: "ico-reports" },
	{ key: "external-services", to: "/external-services", icon: markRaw(GlobeIcon), label: "Web Services", cls: "ico-services" },
];

function loadNavOrder() {
	try {
		const stored = localStorage.getItem(LS_ORDER_KEY);
		if (stored) {
			const keys = JSON.parse(stored) as string[];
			const allKeys = ALL_NAV_ITEMS.map((i) => i.key);
			if (keys.length === allKeys.length && allKeys.every((k) => keys.includes(k)))
				return keys.map((k) => ALL_NAV_ITEMS.find((i) => i.key === k)!);
		}
	} catch {}
	return [...ALL_NAV_ITEMS];
}

const navItems = ref(loadNavOrder());

function saveOrder() {
	localStorage.setItem(LS_ORDER_KEY, JSON.stringify(navItems.value.map((i) => i.key)));
}

const route = useRoute();

const openSub = ref(false);
const showEditorPicker = ref(false);
const showDataPicker = ref(false);
const toast = useToast();

const tourLoading = ref(false);
const sidebarSeen = ref(false);

// const startTour = inject<() => void>("startTour")!;
const startPageTour = inject<() => Promise<boolean>>("startPageTour");
const canStartPageTour = inject<any>("canStartPageTour");

// const sidebarTourRef = ref<any>(null);
const tours = inject<Record<string, any>>("tours")!;
const PAGE = "Sidebar";

function handleDeviceClick() {
	if (!props.isOpen) return;
	openSub.value = !openSub.value;
}

/* ---- tour seen helpers ---- */
async function isSidebarSeen() {
	if (!authIsCached()) return true;
	if (sidebarSeen.value) return true;
	try {
		const { data } = await axios.get("/auth/me/tour", {
			params: { tour: "sidebar", page: PAGE },
		});
		sidebarSeen.value = !!data.seen;
	} catch (e) {
		console.error("[sidebar-tour] GET errore:", e);
		sidebarSeen.value = true;
	}
	return sidebarSeen.value;
}
async function markSidebarSeen() {
	if (!authIsCached() || sidebarSeen.value) return;
	try {
		await axios.post("/auth/me/tour", { tour: "sidebar", page: PAGE });
		sidebarSeen.value = true;
	} catch (e) {
		console.error("[sidebar-tour] POST errore:", e);
	}
}
/** Avvio automatico (first time) */
async function startTourIfNeeded() {
	if (!authIsCached()) return;
	if (await isSidebarSeen()) return;

	// Evita auto-tour in Account
	if (route.name === "account") return;

	// apri submenu e calcola step reali
	openSub.value = true;
	await nextTick();
	computedSidebarSteps.value = getVisibleSidebarSteps();
	if (!computedSidebarSteps.value.length) return;

	const tour = tours?.sidebarTour;
	if (tour?.start) tour.start();
}
function authReady() {
	startTourIfNeeded();
}

/* tour callbacks */
const RAW_SIDEBAR_STEPS = [
	{
		target: ".ico-home",
		content: "Vai alla tua dashboard principale",
		params: { placement: "right" },
	},
	{
		target: ".ico-devices",
		content: "Gestisci tutti i tuoi dispositivi",
		params: { placement: "right" },
	},
	{
		target: ".ico-live-maps",
		content: "Mappa live a tutto schermo",
		params: { placement: "right" },
	},
	{
		target: ".ico-editor",
		content: "Apri l’Editor del device",
		params: { placement: "right" },
	},
	{
		target: ".ico-data",
		content: "Consulta i dati live",
		params: { placement: "right" },
	},
	{
		target: ".ico-account",
		content: "Consulta la pagina del tuo account",
		params: { placement: "right" },
	},
	{
		target: ".ico-alerts",
		content: "Visualizza tutti gli avvisi del sistema",
		params: { placement: "right" },
	},
	{
		target: ".ico-help",
		content: "Riapri l’onboarding quando vuoi",
		params: { placement: "right" },
	},
] as const;

/** Filtra gli step che hanno il target presente nel DOM */
function getVisibleSidebarSteps(): any[] {
	return RAW_SIDEBAR_STEPS.filter((s) => !!document.querySelector(s.target));
}

/** Computed usato sia per bottone sia per avvio */
const computedSidebarSteps = ref<any[]>([]);
const canStartSidebarTour = computed(
	() => computedSidebarSteps.value.length > 0,
);

const canStartHelp = computed(() => {
	const pageOk = !!canStartPageTour?.value;
	return pageOk || canStartSidebarTour.value;
});

async function startHelp() {
	// 1) prova ad avviare la tour "global" della pagina
	try {
		if (startPageTour) {
			const started = await startPageTour();
			if (started) return; // è partita la tour della pagina ✅
			// non è partita: avviso e fallback alla sidebar
			const pageName =
				typeof route.name === "string"
					? route.name.charAt(0).toUpperCase() + route.name.slice(1)
					: "questa pagina";
			toast.info(
				`Nessuna guida disponibile per ${pageName}. Apro la guida relativa alla barra laterale…`,
				{
					position: "top-right",
					duration: 2500,
				},
			);
		}
	} catch (e) {
		console.warn("[help] startPageTour fallita:", e);
	}

	// 2) fallback → tour della sidebar
	await startSidebarTour();

	// 3) opzionale: se anche la sidebar non parte (nessuno step visibile)
	if (!canStartSidebarTour.value) {
		toast.warning("Nessuna guida disponibile in questa vista.", {
			position: "top-right",
			duration: 2500,
		});
	}
}

/** Avvio manuale dal bottone */
async function startSidebarTour() {
	// blocca in Account
	// if (route.name === "account") {
	// 	toast.info("Nessuna guida disponibile in questa pagina.");
	// 	return;
	// }

	// apri submenu per rendere visibili .ico-editor / .ico-data
	if (!openSub.value) openSub.value = true;
	await nextTick();

	// calcola step realmente presenti nel DOM
	computedSidebarSteps.value = getVisibleSidebarSteps();
	if (!computedSidebarSteps.value.length) {
		toast.info("Nessuna guida disponibile in questa pagina.");
		return;
	}

	// manager a nome: tours.sidebarTour
	const tour = tours?.sidebarTour;
	if (!tour || typeof tour.start !== "function") {
		console.warn("[sidebar-tour] manager non disponibile:", tours);
		toast.error("Impossibile avviare la guida in questo momento.");
		return;
	}

	// assicura che il prop :steps sia propagato
	await nextTick();
	try {
		tour.start();
	} catch (e) {
		console.error("[sidebar-tour] errore start():", e);
		toast.error("Si è verificato un problema nell'avvio della guida.");
	}
}

const sidebarCallbacks = {
	onStart() {
		tourLoading.value = true;
		nextTick(() => (tourLoading.value = false));
	},
	onFinish() {
		markSidebarSeen();
		tourLoading.value = false;
	},
	onSkip() {
		markSidebarSeen();
		tourLoading.value = false;
	},
	onError() {
		tourLoading.value = false;
	},
};

async function handleLogout() {
	try {
		const response = await axios.post("/auth/logout");
		if (response.status === 200) {
			setAuth(false);
			sidebarSeen.value = false;
			sessionStorage.removeItem("auth.me");
			toast.info(response.data || "Logout avvenuto con successo", {
				position: "top-right",
				duration: 2000,
			});
			setTimeout(() => router.push("/auth"), 500);
		}
	} catch (error) {
		console.error("Errore durante il logout:", error);
		toast.error("Errore durante il logout. Riprova.", {
			position: "top-right",
			duration: 5000,
		});
	}
}

onMounted(() => {
	// calcola subito se ci sono step visibili (per disabilitare il bottone)
	nextTick(() => (computedSidebarSteps.value = getVisibleSidebarSteps()));
	startTourIfNeeded();
	window.addEventListener("auth-ready", authReady);
});

// aggiorna gli step quando cambia rotta o cambia stato open/sub
watch(
	() => [route.fullPath, props.isOpen, openSub.value],
	() => nextTick(() => (computedSidebarSteps.value = getVisibleSidebarSteps())),
	{ flush: "post" },
);

// ferma tour al cambio pagina (extra safety)
watch(
	() => route.fullPath,
	() => {
		const tour = tours?.sidebarTour;
		tour?.stop?.();
		tourLoading.value = false;
	},
	{ flush: "post" },
);

onBeforeUnmount(() => {
	window.removeEventListener("auth-ready", authReady);
	const tour = tours?.sidebarTour;
	tour?.stop?.();
	tourLoading.value = false;
});
</script>

<style scoped>
/* overlay */
.tour-overlay {
	position: fixed;
	inset: 0;
	z-index: 12000;
	background: rgba(0, 0, 0, 0.4);
	backdrop-filter: blur(2px);
	display: flex;
	align-items: center;
	justify-content: center;
}
.tour-spinner {
	width: 40px;
	height: 40px;
	border: 4px solid rgba(255, 255, 255, 0.25);
	border-top-color: #fff;
	border-radius: 50%;
	animation: tour-spin 1s linear infinite;
}
@keyframes tour-spin {
	from {
		transform: rotate(0);
	}
	to {
		transform: rotate(360deg);
	}
}

/* layout sidebar */
.sidebar {
	position: fixed;
	inset-block: 0;
	left: 0;
	background: #000;
	color: #fff;
	z-index: 10000;
	transition: width 0.25s, transform 0.25s;
	display: flex;
	flex-direction: column;
	border-right: 2px solid #1d3a3a;
	overflow: auto;
}
.sidebar.desk {
	width: 60px;
}
.sidebar.desk.open {
	width: 220px;
}
.sidebar.desk:not(.open) .logo {
	display: none;
}

@media (max-width: 1023px) {
	.sidebar {
		width: 220px;
		transform: translateX(-100%);
	}
	.sidebar.open {
		transform: none;
	}
	body.hist-open .sidebar.open {
		transform: translateX(-100%) !important;
	}
}

/* header */
.hdr {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem 1rem;
}
.logo img {
	max-width: 120px;
	height: auto;
	object-fit: contain;
	width: 100%;
}
.tgl {
	background: none;
	border: 0;
	color: #fff;
	font-size: 1.6rem;
	cursor: pointer;
	margin-left: auto;
}

/* quando la sidebar desktop è chiusa: impila e ordina -> logo, hamburger, campanella */
.sidebar.desk:not(.open) .hdr {
	flex-direction: column;
	align-items: center;
	gap: 0.35rem;
	padding: 0.5rem 0;
}
.sidebar.desk:not(.open) .logo {
	order: 1;
}
.sidebar.desk:not(.open) .tgl {
	order: 2;
	margin-left: 0;
}
.sidebar.desk:not(.open) .hdr .nb-wrap {
	order: 3;
	margin-top: 4px;
}

/* menu */
.menu {
	flex: 1 1 auto;
}
.item {
	display: flex;
	align-items: center;
	gap: 0.8rem;
	padding: 0.8rem 1rem;
	color: #fff;
	text-decoration: none;
	transition: background 0.15s;
	width: 100%;
	box-sizing: border-box;
}
.item:hover,
.item:focus-visible {
	background: #008f8f;
}
.item:active {
	background: #007777;
}
.item.router-link-active {
	background: #009c9c;
}
.logo.router-link-active {
	background: transparent;
}

.sub {
	background: #111;
}
.sub-i {
	padding-left: 2.5rem;
}

.ico {
	width: 18px;
	height: 18px;
}

.nav-row {
	display: block;
}

.rot {
	transform: rotate(90deg);
	transition: transform 0.2s;
}

.logout {
	margin-top: auto;
	color: #feb2b2;
}
.logout:hover {
	background: #e53e3e;
	color: #fff;
}

/* hamburger mobile */
.hamb {
	position: fixed;
	top: 0.7rem;
	left: 0.7rem;
	z-index: 1200000;
	cursor: pointer;
	width: 30px;
	height: 30px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #fff;
}
</style>
